import React, { useState, useEffect } from 'react';
import { Search, Users, UserCheck, UserPlus, Heart, Eye } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { followsService } from '../services/followsService';
import FollowButton from './FollowButton';
import { ArtCategory } from '../types';

const FollowingView: React.FC = () => {
  const { user } = useAuth();
  const { select } = useLanguage();
  const categoryLabels: Record<ArtCategory, string> = {
    music: select({ es: 'Música', en: 'Music' }),
    writing: select({ es: 'Escritura', en: 'Writing' }),
    tattoo: select({ es: 'Tatuajes', en: 'Tattoos' }),
    digital_design: select({ es: 'Diseño Digital', en: 'Digital Design' }),
    fashion_design: select({ es: 'Diseño de Moda', en: 'Fashion Design' }),
    photography: select({ es: 'Fotografía', en: 'Photography' }),
    sculpture: select({ es: 'Escultura', en: 'Sculpture' }),
    painting: select({ es: 'Pintura', en: 'Painting' }),
    dance: select({ es: 'Danza', en: 'Dance' }),
    filmmaking: select({ es: 'Cine', en: 'Filmmaking' }),
    makeup: select({ es: 'Maquillaje', en: 'Makeup' }),
  };
  const [activeTab, setActiveTab] = useState<'following' | 'followers' | 'discover'>('following');
  const [searchQuery, setSearchQuery] = useState('');
  const [following, setFollowing] = useState<Record<string, unknown>[]>([]);
  const [followers, setFollowers] = useState<Record<string, unknown>[]>([]);
  const [recommendedUsers, setRecommendedUsers] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [followCounts, setFollowCounts] = useState({ followers: 0, following: 0 });

  const formatStat = (
    count: number,
    esSingular: string,
    esPlural: string,
    enSingular: string,
    enPlural: string
  ) => {
    const isPlural = count !== 1;
    return select({
      es: `${count} ${isPlural ? esPlural : esSingular}`,
      en: `${count} ${isPlural ? enPlural : enSingular}`,
    });
  };

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [followingData, followersData, recommendedData, counts] = await Promise.all([
        followsService.getFollowing(user!.id),
        followsService.getFollowers(user!.id),
        followsService.getRecommendedUsers(10),
        followsService.getFollowCounts(user!.id)
      ]);

      setFollowing(followingData);
      setFollowers(followersData);
      setRecommendedUsers(recommendedData);
      setFollowCounts(counts);
    } catch (err) {
      console.error('Error loading follow data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFollowChange = () => {
    // Reload data when follow status changes
    loadData();
  };

  const visitProfile = (username: string) => {
    // Navigate to user profile - you might want to implement this differently
    window.location.href = `/profile/${username}`;
  };

  const filteredFollowing = following.filter(user =>
    user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.full_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredFollowers = followers.filter(user =>
    user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.full_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const UserCard: React.FC<{ userData: Record<string, unknown>; showFollowButton?: boolean }> = ({ 
    userData, 
    showFollowButton = true 
  }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-4">
        <div 
          className="cursor-pointer hover:scale-105 transition-transform"
          onClick={() => visitProfile(userData.username)}
        >
          <img
            src={userData.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=64&h=64&fit=crop'}
            alt={userData.full_name}
            className="w-16 h-16 rounded-full object-cover"
          />
        </div>
        
        <div className="flex-1">
          <div 
            className="cursor-pointer hover:text-purple-600 transition-colors"
            onClick={() => visitProfile(userData.username)}
          >
            <h3 className="font-semibold text-gray-900 text-lg">{userData.full_name}</h3>
            <p className="text-gray-500">@{userData.username}</p>
          </div>
          
          {userData.bio && (
            <p className="text-gray-700 text-sm mt-1 line-clamp-2">{userData.bio}</p>
          )}
          
          {userData.art_categories && userData.art_categories.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {userData.art_categories.slice(0, 2).map((category: ArtCategory) => (
                <span
                  key={category}
                  className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full"
                >
                  {categoryLabels[category]}
                </span>
              ))}
            {userData.art_categories.length > 2 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                  {select({
                    es: `+${userData.art_categories.length - 2} más`,
                    en: `+${userData.art_categories.length - 2} more`,
                  })}
              </span>
            )}
          </div>
        )}
          
          {userData.location && (
            <p className="text-gray-500 text-sm mt-1">📍 {userData.location}</p>
          )}
        </div>
        
        <div className="flex flex-col gap-2">
          {showFollowButton && (
            <FollowButton 
              userId={userData.id} 
              username={userData.username}
              size="md"
              variant="primary"
              onFollowChange={handleFollowChange}
            />
          )}
          
          <button
            onClick={() => visitProfile(userData.username)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
          >
            {select({ es: 'Ver perfil', en: 'View profile' })}
          </button>
        </div>
      </div>
      
      {/* Quick stats if available */}
      {userData.stats && (
        <div className="flex items-center gap-6 mt-4 pt-4 border-t border-gray-100 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <Eye size={14} />
            <span>{formatStat(userData.stats.posts || 0, 'obra', 'obras', 'work', 'works')}</span>
          </div>
          <div className="flex items-center gap-1">
            <Heart size={14} />
            <span>{formatStat(userData.stats.likes || 0, 'like', 'likes', 'like', 'likes')}</span>
          </div>
          <div className="flex items-center gap-1">
            <Users size={14} />
            <span>{formatStat(userData.stats.followers || 0, 'seguidor', 'seguidores', 'follower', 'followers')}</span>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-gray-200 h-24 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Users className="text-purple-600" size={24} />
          {select({ es: 'Comunidad', en: 'Community' })}
        </h1>
        <p className="text-gray-600 mt-1">
          {select({ es: 'Conecta con artistas y descubre talento', en: 'Connect with artists and discover new talent' })}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg p-4 text-white">
          <div className="flex items-center gap-2">
            <UserCheck size={20} />
            <div>
              <p className="text-blue-100 text-sm">{select({ es: 'Siguiendo', en: 'Following' })}</p>
              <p className="text-xl font-bold">{followCounts.following}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg p-4 text-white">
          <div className="flex items-center gap-2">
            <Users size={20} />
            <div>
              <p className="text-purple-100 text-sm">{select({ es: 'Seguidores', en: 'Followers' })}</p>
              <p className="text-xl font-bold">{followCounts.followers}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg p-4 text-white">
          <div className="flex items-center gap-2">
            <UserPlus size={20} />
            <div>
              <p className="text-green-100 text-sm">{select({ es: 'Recomendados', en: 'Recommended' })}</p>
              <p className="text-xl font-bold">{recommendedUsers.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-lg p-4 text-white">
          <div className="flex items-center gap-2">
            <Heart size={20} />
            <div>
              <p className="text-orange-100 text-sm">{select({ es: 'Total', en: 'Total' })}</p>
              <p className="text-xl font-bold">{followCounts.following + followCounts.followers}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder={select({ es: 'Buscar usuarios...', en: 'Search users...' })}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('following')}
            className={`py-3 px-1 border-b-2 transition-colors ${
              activeTab === 'following'
                ? 'border-purple-500 text-purple-600 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {select({ es: 'Siguiendo', en: 'Following' })} ({followCounts.following})
          </button>
          <button
            onClick={() => setActiveTab('followers')}
            className={`py-3 px-1 border-b-2 transition-colors ${
              activeTab === 'followers'
                ? 'border-purple-500 text-purple-600 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {select({ es: 'Seguidores', en: 'Followers' })} ({followCounts.followers})
          </button>
          <button
            onClick={() => setActiveTab('discover')}
            className={`py-3 px-1 border-b-2 transition-colors ${
              activeTab === 'discover'
                ? 'border-purple-500 text-purple-600 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {select({ es: 'Descubrir', en: 'Discover' })} ({recommendedUsers.length})
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="space-y-4">
        {activeTab === 'following' && (
          <>
            {filteredFollowing.length > 0 ? (
              filteredFollowing.map((userData) => (
                <UserCard 
                  key={userData.id} 
                  userData={userData} 
                  showFollowButton={true}
                />
              ))
            ) : (
              <div className="text-center py-12">
                <UserCheck size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {searchQuery
                    ? select({ es: 'No se encontraron usuarios', en: 'No users found' })
                    : select({ es: 'No sigues a nadie aún', en: 'You are not following anyone yet' })}
                </h3>
                <p className="text-gray-500 mb-4">
                  {searchQuery
                    ? select({ es: 'Intenta con otros términos de búsqueda', en: 'Try different search terms' })
                    : select({ es: 'Explora artistas y empieza a seguir a quienes te inspiren', en: 'Explore artists and start following those who inspire you' })}
                </p>
                {!searchQuery && (
                  <button 
                    onClick={() => setActiveTab('discover')}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    {select({ es: 'Descubrir artistas', en: 'Discover artists' })}
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === 'followers' && (
          <>
            {filteredFollowers.length > 0 ? (
              filteredFollowers.map((userData) => (
                <UserCard 
                  key={userData.id} 
                  userData={userData} 
                  showFollowButton={true}
                />
              ))
            ) : (
              <div className="text-center py-12">
                <Users size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {searchQuery
                    ? select({ es: 'No se encontraron usuarios', en: 'No users found' })
                    : select({ es: 'No tienes seguidores aún', en: 'You do not have followers yet' })}
                </h3>
                <p className="text-gray-500 mb-4">
                  {searchQuery
                    ? select({ es: 'Intenta con otros términos de búsqueda', en: 'Try different search terms' })
                    : select({ es: 'Comparte contenido increíble para atraer seguidores', en: 'Share amazing work to attract followers' })}
                </p>
                {!searchQuery && (
                  <button 
                    onClick={() => window.location.href = '/create'}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    {select({ es: 'Crear primera obra', en: 'Create your first piece' })}
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === 'discover' && (
          <>
            {recommendedUsers.length > 0 ? (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h3 className="font-medium text-blue-900 mb-2">
                    {select({ es: '✨ Descubre nuevos artistas', en: '✨ Discover new artists' })}
                  </h3>
                  <p className="text-sm text-blue-700">
                    {select({
                      es: 'Estos artistas pueden interesarte basado en tus gustos y actividad',
                      en: 'These artists might interest you based on your tastes and activity',
                    })}
                  </p>
                </div>
                
                {recommendedUsers.map((userData) => (
                  <UserCard 
                    key={userData.id} 
                    userData={userData} 
                    showFollowButton={true}
                  />
                ))}
              </>
            ) : (
              <div className="text-center py-12">
                <UserPlus size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {select({ es: 'No hay recomendaciones', en: 'No recommendations yet' })}
                </h3>
                <p className="text-gray-500">
                  {select({
                    es: 'Interactúa más con la comunidad para recibir mejores recomendaciones',
                    en: 'Engage more with the community to receive better recommendations',
                  })}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Quick Actions */}
      <div className="mt-8 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {select({ es: '🚀 Construye tu red', en: '🚀 Build your network' })}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h3 className="font-medium text-gray-900 mb-2">
              {select({ es: 'Para artistas:', en: 'For artists:' })}
            </h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• {select({ es: 'Sigue artistas que te inspiren', en: 'Follow artists who inspire you' })}</li>
              <li>• {select({ es: 'Colabora en proyectos creativos', en: 'Collaborate on creative projects' })}</li>
              <li>• {select({ es: 'Intercambia técnicas y consejos', en: 'Exchange techniques and tips' })}</li>
            </ul>
          </div>
          <div>
            <h3 className="font-medium text-gray-900 mb-2">
              {select({ es: 'Para coleccionistas:', en: 'For collectors:' })}
            </h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• {select({ es: 'Descubre talentos emergentes', en: 'Discover emerging talent' })}</li>
              <li>• {select({ es: 'Mantente al día con nuevas obras', en: 'Stay up to date with new pieces' })}</li>
              <li>• {select({ es: 'Conecta directamente con artistas', en: 'Connect directly with artists' })}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FollowingView;
